import medicalHero480 from '../assets/imgs/landing/negligencia-medica-hero-480.webp';
import medicalHero800 from '../assets/imgs/landing/negligencia-medica-hero-800.webp';
import medicalHero1200 from '../assets/imgs/landing/negligencia-medica-hero-1200.webp';
import medicalSituationImage from '../assets/imgs/inicio/1.webp';
import medicalTeam480 from '../assets/imgs/landing/negligencia-medica-equipo-480.webp';
import medicalTeam800 from '../assets/imgs/landing/negligencia-medica-equipo-800.webp';
import medicalTeam1200 from '../assets/imgs/landing/negligencia-medica-equipo-1200.webp';
import medicalOffice480 from '../assets/imgs/landing/negligencia-medica-oficina-480.webp';
import medicalOffice800 from '../assets/imgs/landing/negligencia-medica-oficina-800.webp';
import medicalOffice1080 from '../assets/imgs/landing/negligencia-medica-oficina-1080.webp';
import founderImage from '../assets/imgs/landing/aldo-polack-192.webp';
import type { IconName } from '../components/Icon';

export const MEDICAL_NEGLIGENCE_WHATSAPP_MESSAGE =
  'Buen día, quisiera contarles mi caso relacionado con una posible negligencia médica.';

export type LandingTestimonial = {
  quote: string;
  author: string;
  detail?: string;
};

// Agregar aquí únicamente testimonios reales, confirmados y autorizados.
// La sección no se renderiza mientras esta colección permanezca vacía.
export const MEDICAL_NEGLIGENCE_TESTIMONIALS: readonly LandingTestimonial[] = [];

export const MEDICAL_NEGLIGENCE_LANDING = {
  hero: {
    image: medicalHero800,
    imageSrcSet: `${medicalHero480} 480w, ${medicalHero800} 800w, ${medicalHero1200} 1200w`,
    imageSizes: '(max-width: 640px) calc(100vw - 32px), (max-width: 900px) 680px, 42vw',
    imageWidth: 800,
    imageHeight: 597,
    eyebrow: 'Negligencia médica · Corporación Indemniza',
    title: '¿Sufriste un daño por una posible negligencia médica?',
    focus:
      'Atendemos especialmente posibles casos ocurridos en clínicas privadas y con médicos particulares.',
    description:
      'Especialistas en reparaciones y compensaciones. Cuéntanos qué ocurrió y nuestro equipo legal y técnico realizará una revisión preliminar de la información para indicarte cómo avanzar.',
  },
  trustItems: [
    {
      icon: 'hourglass' as IconName,
      title: 'Más de 25 años de experiencia',
      description: 'Trayectoria en materia indemnizatoria',
    },
    {
      icon: 'scale' as IconName,
      title: 'Especialistas en indemnizaciones',
      description: 'Reparación de daños y perjuicios',
    },
    {
      icon: 'document' as IconName,
      title: 'Equipo técnico pericial',
      description: 'Soporte especializado para cada caso',
    },
  ],
  privateCare: {
    eyebrow: 'Atención médica privada',
    title: 'Casos relacionados con clínicas privadas y médicos particulares',
    description:
      'Cuando existen daños o consecuencias después de una atención médica privada, es necesario revisar cada situación antes de determinar responsabilidades y posibles alternativas.',
    items: [
      {
        icon: 'document' as IconName,
        title: 'Historia clínica y documentación',
        description: 'Revisamos la historia clínica y la documentación disponible para comprender los hechos y la atención recibida.',
      },
      {
        icon: 'scale' as IconName,
        title: 'Responsabilidades posibles',
        description: 'Analizamos la actuación médica y la posible participación de la clínica, el médico, una aseguradora u otros intervinientes, según el caso.',
      },
      {
        icon: 'medical' as IconName,
        title: 'Daño y soporte técnico pericial',
        description: 'Evaluamos el daño alegado y qué revisión médica, técnica o pericial puede ser necesaria para sustentar el análisis.',
      },
      {
        icon: 'handshake' as IconName,
        title: 'Alternativas para avanzar',
        description: 'Según las circunstancias, pueden evaluarse negociación, conciliación u otras acciones legales que correspondan.',
      },
    ],
    note: 'Cada caso requiere una evaluación individual de los hechos y de la documentación disponible.',
  },
  response: {
    image: medicalSituationImage,
    eyebrow: 'Cuando algo salió mal',
    title: 'Pucha, ¿y ahora?',
    emphasis: 'Mejor llama a Indemniza',
    description:
      'Si después de una atención médica quedaron dudas, daños o consecuencias que necesitas entender, cuéntanos qué pasó. El primer paso es conversar y revisar la información inicial.',
  },
  team: {
    image: medicalTeam800,
    imageSrcSet: `${medicalTeam480} 480w, ${medicalTeam800} 800w, ${medicalTeam1200} 1200w`,
    imageSizes: '(max-width: 640px) calc(100vw - 32px), (max-width: 900px) 700px, 42vw',
    imageWidth: 800,
    imageHeight: 447,
    title: 'Equipo multidisciplinario de primer nivel',
    description:
      'Los casos de indemnización pueden requerir una mirada legal, médica, técnica y humana. Indemniza articula especialistas de distintas disciplinas según las necesidades de cada caso.',
    capabilities: [
      {
        icon: 'scale' as IconName,
        title: 'Equipo legal',
        description: 'Abogados egresados de la Pontificia Universidad Católica del Perú y experiencia en materia indemnizatoria.',
      },
      {
        icon: 'document' as IconName,
        title: 'Equipo técnico y pericial',
        description: 'Peritos especializados y acreditados para sustentar técnicamente los casos cuando corresponde.',
      },
      {
        icon: 'medical' as IconName,
        title: 'Especialistas según el caso',
        description: 'Profesionales de materia médica, psicológica y otras disciplinas requeridas para una evaluación integral.',
      },
    ],
  },
  founder: {
    image: founderImage,
    imageWidth: 192,
    imageHeight: 240,
    name: 'Aldo Polack Cavassa',
    role: 'Socio Fundador',
    area: 'Indemnizaciones y Daños',
  },
  process: [
    {
      step: '01',
      title: 'Cuéntanos tu caso',
      description: 'Puedes escribirnos, enviarnos un audio o compartir un breve documento por WhatsApp contándonos qué ocurrió y cómo te afectó a ti o a tu familiar.',
    },
    {
      step: '02',
      title: 'Hacemos una revisión preliminar',
      description: 'Revisamos la información inicial para entender la situación y determinar qué especialistas podrían ser necesarios.',
    },
    {
      step: '03',
      title: 'Te explicamos cómo continuar',
      description: 'Si el caso requiere una reunión, evaluación detallada u otro servicio profesional, te informamos previamente el alcance y las condiciones.',
    },
  ],
  office: {
    image: medicalOffice800,
    imageSrcSet: `${medicalOffice480} 480w, ${medicalOffice800} 800w, ${medicalOffice1080} 1080w`,
    imageSizes: '(max-width: 640px) calc(100vw - 32px), (max-width: 900px) calc(100vw - 48px), 46vw',
    imageWidth: 800,
    imageHeight: 1071,
    title: 'Dónde estamos',
    description:
      'Contamos con atención presencial en Santiago de Surco. Conoce nuestra ubicación y, si lo prefieres, revisa también la web institucional de Corporación Indemniza.',
  },
  pricingMessage: 'Contamos con planes de trabajo flexibles de acuerdo al caso.',
  faqs: [
    {
      question: '¿Tiene costo contarles inicialmente mi caso?',
      answer:
        'No. Puedes compartir la información inicial de tu situación para una revisión preliminar rápida. Si luego se requiere una reunión, evaluación detallada u otro servicio profesional, el equipo te informará previamente el alcance y las condiciones.',
    },
    {
      question: '¿Qué información conviene tener para el primer contacto?',
      answer:
        'Puedes empezar con un mensaje de texto, un audio o un breve documento por WhatsApp. No necesitas preparar un expediente completo para este primer contacto; luego te indicaremos qué documentación puede ser relevante revisar.',
    },
    {
      question: '¿Puedo consultar por el caso de un familiar?',
      answer:
        'Sí. Puedes explicar lo ocurrido en nombre de un familiar y el equipo te indicará qué información será necesaria para continuar.',
    },
    {
      question: '¿Qué ocurre después de contar mi caso?',
      answer:
        'Realizamos una revisión preliminar de la información inicial. Si corresponde continuar, te explicaremos los siguientes pasos y qué tipo de evaluación o especialista puede ser necesario.',
    },
    {
      question: '¿Se garantiza obtener una indemnización?',
      answer:
        'No. Cada situación depende de sus circunstancias, evidencia y sustento. Indemniza no garantiza resultados ni montos de indemnización.',
    },
  ],
} as const;
